import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Plus, ArrowRight, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { SmartImage } from "./ui/SmartImage";
import { formatPrice } from "../lib/filters";

const AddonRow = ({ addon, parentLid }) => {
  const { isAddonInCart, toggleAddon } = useCart();
  const added = isAddonInCart(addon.id, parentLid);
  return (
    <div
      data-testid={`addon-row-${addon.id}`}
      className="flex items-center gap-4 py-4 border-b border-beige/70"
    >
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-sm bg-stone">
        <SmartImage src={addon.image} alt={addon.name} label={addon.name} loading="lazy" className="h-full w-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-charcoal leading-tight">{addon.name}</p>
        <p className="text-xs text-taupe mt-0.5">{addon.finish}</p>
        <p className="text-sm font-serif text-charcoal mt-1">{formatPrice(addon.price)}</p>
      </div>
      <motion.button
        whileTap={{ scale: 0.94 }}
        onClick={() => toggleAddon(addon, parentLid)}
        data-testid={`addon-add-${addon.id}`}
        aria-pressed={added}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-xs font-medium transition-colors duration-300 shrink-0 ${
          added
            ? "bg-charcoal text-ivory"
            : "border border-charcoal text-charcoal hover:bg-stone/60"
        }`}
      >
        {added ? <Check size={14} strokeWidth={2} /> : <Plus size={14} strokeWidth={2} />}
        {added ? "Added" : "Add to order"}
      </motion.button>
    </div>
  );
};

export const AddOnDrawer = ({ open, onOpenChange, product, parentLid, addons }) => {
  const navigate = useNavigate();
  const { subtotal } = useCart();
  if (!product) return null;

  const taps = addons?.taps ?? [];
  const waste = addons?.waste ?? null;
  const tagline = addons?.tagline || "Pairs well with your vanity";

  const goToCart = () => {
    onOpenChange(false);
    setTimeout(() => navigate("/cart"), 120);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="addon-drawer"
        className="w-full sm:max-w-md bg-ivory border-beige p-0 flex flex-col gap-0"
      >
        <div className="px-6 pt-6 pb-4 border-b border-beige">
          <SheetTitle className="sr-only">Complete your vanity setup</SheetTitle>
          <p
            data-testid="addon-confirm"
            className="inline-flex items-center gap-2 text-xs text-charcoal/70 bg-stone/70 border border-beige rounded-full px-3 py-1.5"
          >
            <Check size={13} strokeWidth={2} className="text-charcoal" />
            {product.name} added
          </p>
          <h2 className="font-serif font-light text-charcoal text-3xl leading-tight mt-4">
            Complete your set
          </h2>
          <p className="eyebrow text-taupe mt-2 flex items-center gap-2">
            <Sparkles size={13} strokeWidth={1.6} /> {tagline}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {taps.length > 0 && (
            <>
              <p className="eyebrow text-taupe mb-1">Washbasin tap</p>
              {taps.map((t) => (
                <AddonRow key={t.id} addon={t} parentLid={parentLid} />
              ))}
            </>
          )}
          {waste && (
            <>
              <p className="eyebrow text-taupe mb-1 mt-6">Waste coupling</p>
              <AddonRow addon={waste} parentLid={parentLid} />
            </>
          )}
        </div>

        <div className="border-t border-beige px-6 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-charcoal/70">Order total</span>
            <span data-testid="addon-order-total" className="font-serif text-2xl text-charcoal">
              {formatPrice(subtotal)}
            </span>
          </div>
          <button
            onClick={goToCart}
            data-testid="addon-continue"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
          >
            Continue to cart <ArrowRight size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="addon-skip"
            className="w-full rounded-full border border-beige py-3.5 text-sm font-medium text-charcoal hover:border-charcoal transition-colors duration-300"
          >
            Skip for now
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
